import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useNotification from '../../wrappers/use-notification';
import NotificationList from '../../components/NotificationList';
import { NotificationFilters } from '../../../../shared/types/notification.types';
import { notificationsPageStyles } from './Notifications.styles';

const Notifications: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { list, edit } = useNotification();
    const [filter, setFilter] = useState<'all' | 'unread'>('unread');
    const [listKey, setListKey] = useState(0);
    const [markingAll, setMarkingAll] = useState(false);

    const baseFilters: NotificationFilters = {
        userUid: user?.uid,
        garageUid: user?.garageUid,
        dealerId: user?.dealerId,
        role: user?.role,
    };

    const filters: NotificationFilters = {
        ...baseFilters,
        ...(filter === 'unread' ? { read: false } : {}),
    };

    const handleMarkAllAsRead = async () => {
        setMarkingAll(true);
        try {
            const unread = await list({ ...baseFilters, read: false });
            await Promise.all(
                unread.map(n => edit(n.id, { read: true, readAt: new Date(), readBy: user?.uid }))
            );
            setListKey(k => k + 1);
        } finally {
            setMarkingAll(false);
        }
    };

    return (
        <Box sx={notificationsPageStyles.root}>
            <Box sx={notificationsPageStyles.header}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>{t('notifications.title')}</Typography>
                    <Typography color="text.secondary" mt={0.5}>{t('notifications.subtitle')}</Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleMarkAllAsRead}
                    disabled={markingAll}
                    startIcon={markingAll ? <CircularProgress size={14} /> : undefined}
                >
                    {t('notifications.markAllRead')}
                </Button>
            </Box>

            <Paper elevation={0} sx={notificationsPageStyles.filtersPaper}>
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={(_, v) => v && setFilter(v)}
                    size="small"
                >
                    <ToggleButton value="unread" sx={notificationsPageStyles.toggleButton}>{t('notifications.filterUnread')}</ToggleButton>
                    <ToggleButton value="all" sx={notificationsPageStyles.toggleButton}>{t('notifications.filterAll')}</ToggleButton>
                </ToggleButtonGroup>
            </Paper>

            <Paper elevation={0} sx={notificationsPageStyles.listPaper}>
                <NotificationList
                    key={listKey}
                    filters={filters}
                    onRead={() => setListKey(k => k + 1)}
                />
            </Paper>
        </Box>
    );
};

export default Notifications;
